import type { DrillTarget } from './workflow-drill-context';
import { workflowTriggerFlowNodeId } from './workflow-flow-node-ids';

/**
 * The canvas flow-node id an open inspector target belongs to, or `null` when
 * nothing is open.
 *
 * The inspector is opened from a click on a card, which already knows both the
 * target and the id it came from. This recovers the id from the target alone, so
 * the canvas can ask "is the thing I'm editing still on the graph?" without
 * having to keep a second piece of state in step with the first.
 *
 * Mirrors the ids `workflowGraphToFlow` builds: a node keeps its graph id, a
 * trigger is namespaced (triggers aren't workflow nodes).
 */
export function workflowInspectorTargetFlowNodeId(
  target: DrillTarget
): string | null {
  if (target === null) {
    return null;
  }
  if (target.kind === 'trigger') {
    return workflowTriggerFlowNodeId(target.data.trigger.id);
  }
  return target.node.id;
}

/**
 * Whether an open inspector target still exists, and so should stay open.
 *
 * A node is live while it is in the *full graph* — including a for-each loop
 * body, which is hidden from the canvas but editable in the inspector. Checking
 * only the visible flow would wrongly close that body's config the instant it
 * opens; only a genuine delete (gone from the graph) should close it. A trigger
 * has no graph node, so it is live while its synthesized flow node is present.
 *
 * `null` (nothing open) counts as live — there is nothing to close.
 */
export function workflowInspectorTargetIsLive(
  target: DrillTarget,
  graphNodeIds: ReadonlySet<string>,
  flowNodeIds: ReadonlySet<string>
): boolean {
  if (target === null) {
    return true;
  }
  if (target.kind === 'trigger') {
    return flowNodeIds.has(workflowTriggerFlowNodeId(target.data.trigger.id));
  }
  return graphNodeIds.has(target.node.id);
}
